import React, { useMemo } from 'react';
import Lozenge from '@atlaskit/lozenge';
import { Provider as SmartCardProvider, Client } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	Sections,
	colors,
	type SectionData,
	type SectionRendererArgs,
	type ConfigurableDate,
	VIEW_MODES,
} from '@atlassian/jira-portfolio-3-common';
import { ADD_FIELDS_COLUMN_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/view-settings/field-columns/types';
import {
	getConfiguredDateTooltip,
	getHeaderMessage,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/fields/messages';
import type { IssueColumn } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/fields/types';
import {
	DEFAULT_FIELD_WIDTH,
	SECTION_KEYS,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import Column from './column';
import Empty from './empty';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types';
import { showEmptyView } from './utils';

const getColumnTitle = (column: IssueColumn): string => {
	if (typeof column.title === 'string') {
		return column.title;
	}
	// @ts-expect-error Type 'MessageFormatElement[]' is not assignable to type 'string'.
	return column.title.defaultMessage || '';
};

export const Columns = ({
	columns,
	height,
	width,
	getMatchingConfiguredDate,
	areCustomFieldsReady,
	viewNamespace,
	onDragStart,
	onDragEnd,
	items,
	isReadOnly,
	showOptimizations = false,
	scrollElement,
	showWarning,
	planningUnit,
	viewMode,
	minWidth,
	isExportMode,
	isConfluenceMacro,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isViewModeList = viewMode === VIEW_MODES.LIST;

	const onHandleDragEnd = () => {
		const analyticsEvent = createAnalyticsEvent({ action: 'dragged', actionSubject: 'column' });
		fireUIAnalytics(analyticsEvent, 'fields', { viewMode });

		onDragEnd();
	};

	const renderColumn = (column: IssueColumn, section: SectionData, isDragging?: boolean) => {
		const { id, cell, attribute } = column;
		return (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			<div style={{ width: '100%' }}>
				<Column
					key={id}
					id={id}
					attribute={attribute}
					cell={cell}
					items={items}
					isReadOnly={isReadOnly || column.isReadOnly || false}
					showOptimizations={showOptimizations}
					scrollElement={scrollElement}
					width={section.width}
					isDragging={isDragging}
					showWarning={showWarning}
				/>
			</div>
		);
	};

	const getColumnTextTitle = (section: SectionData) => {
		const titleLabel = section.id && getHeaderMessage(section.id, planningUnit);
		if (titleLabel) {
			return formatMessage(titleLabel);
		}
		return section.title;
	};

	const dateIcon = useMemo(() => <Lozenge>D</Lozenge>, []);

	const sectionRenderer =
		(issueColumns: IssueColumn[]) =>
		// False-positive :/
		// eslint-disable-next-line react/no-unstable-nested-components
		({
			section,
			onSectionClick,
			index,
			isDragging,
			hoveredSectionIndex,
			setHovered,
		}: SectionRendererArgs) => {
			const icon = dateIcon;
			const tooltip = icon && getConfiguredDateTooltip(section.configurableDate);

			return (
				<Sections.Item
					backgroundColor={
						issueColumns[index].hasOptimizedValues
							? undefined
							: token('color.background.neutral.subtle', colors.N0)
					}
					hoveredSectionIndex={hoveredSectionIndex}
					icon={section.configurableDate && icon}
					id={section.id}
					index={index}
					key={`section-${section.id}`}
					minWidth={section.minWidth}
					onClick={onSectionClick}
					setHovered={setHovered}
					title={getColumnTextTitle(section)}
					tooltip={tooltip && formatMessage(tooltip, { title: section.title })}
					width={section.width}
					collapsible={section.collapsible}
				>
					{renderColumn(issueColumns[index], section, isDragging)}
				</Sections.Item>
			);
		};

	const visibleColumns = columns.filter((column) => column.isVisible);

	if (
		showEmptyView({
			areCustomFieldsReady,
			isViewModeList,
			isExportMode,
			isConfluenceMacro,
			totalVisibleColumns: visibleColumns.filter(({ id }) => id !== ADD_FIELDS_COLUMN_ID).length,
		})
	) {
		return (
			<Empty
				columns={columns}
				getMatchingConfiguredDate={getMatchingConfiguredDate}
				width={minWidth ?? width}
			/>
		);
	}

	if (!areCustomFieldsReady || visibleColumns.length === 0) {
		return null;
	}

	const sectionsData = visibleColumns.map((column, index, arr) => {
		const data: {
			configurableDate?: ConfigurableDate;
			initialWidth: undefined | number;
			isFixed: boolean;
			minWidth: number;
			id: string;
			title: string;
			collapsible: boolean | undefined;
		} = {
			initialWidth: column.defaultWidth,
			minWidth: column.minWidth ?? 24,
			// Last column is flexible to absorb the remaining delta
			isFixed: !(viewMode === VIEW_MODES.LIST && index === arr.length - 1),
			id: column.id,
			title: getColumnTitle(column),
			collapsible: column.isCollapsible,
		};

		const configurableDate = getMatchingConfiguredDate(column.id);
		if (configurableDate) {
			data.configurableDate = configurableDate;
			data.initialWidth = DEFAULT_FIELD_WIDTH.LARGE;
		}

		return data;
	});

	const linkToParent = isViewModeList ? SECTION_KEYS.LIST_MAIN : SECTION_KEYS.MAIN;
	const id = isViewModeList ? SECTION_KEYS.LIST_FIELD : SECTION_KEYS.FIELD;

	const columnContent = (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div className={styles.container}>
			<Sections
				namespace={viewNamespace}
				/**
				 * CAVEAT of 'linkToParent' with 'isFitParent'
				 *
				 * There are three "Fields" sections (sticky-header, header, and columns),
				 * only ONE section should be linked to the parent IF the sections have `isFitParent=false`.
				 *
				 * `isFitParent=false` where the section does not bound by parent's size, the parent's width get updated after the section resized,
				 * this will resulted with multiple cascading updates on the parent section IF multiple sections linked to the same parent.
				 *
				 * */
				linkToParent={linkToParent}
				// @ts-expect-error - TS2322 - Type '{ namespace: string; linkToParent: "main-sections"; minHeight: string; height: string | number; id: "fields-section"; sectionRenderer: ({ section, onSectionClick, index, isDragging, hoveredSectionIndex, setHovered, }: SectionRendererArgs) => Element; sectionsData: { ...; }[]; onDragStart: () => void; onDragEnd: () =...' is not assignable to type 'IntrinsicAttributes & NamespacedIdProps'.
				minHeight="100%"
				height={height || '100%'}
				id={id}
				sectionRenderer={sectionRenderer(visibleColumns)}
				sectionsData={sectionsData}
				onDragStart={onDragStart}
				onDragEnd={onHandleDragEnd}
				width={width}
				minWidth={minWidth}
				isFitParent={!isViewModeList}
			/>
		</div>
	);

	return <SmartCardProvider client={new Client()}>{columnContent}</SmartCardProvider>;
};

export default Columns;
